import React, { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { CellProps, Column, Row } from 'react-table';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { SwitchInput } from '../../../../../components/form/SwitchInput';
import { DropdownActionItem } from '../../../../../atomic/molecules/DropdownActionItem';
import { EmptyState } from '../../../components/EmptyStateCard';
import { Star } from '../../../components/Star';
import { TableContent } from '../../../manager/components/TableContent';
import { SkillLevelSelect } from '../common/SkillLevelSelect';
import { SkillNameWithIcon } from '../common/SkillNameWithIcon';
import { EmployeeSkillAssociation, SkillLevel } from './types';
import { filterByCategoryIds } from './mappings';
import { DropdownActions } from '../../../../../atomic/templates/DropdownActions';
import { canSelfAddRemove, canSelfRate } from './skillSelfServiceMode';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import { MonthsExperienceInput } from './MonthsExperienceInput';
import { formatMonthsExperience } from './monthsExperience';
import { AutoSaveIndicator } from '../../../../../components/form/AutoSaveIndicator';
import { SkillCategoryFilter } from '../manager/SkillCategoryFilter';
import { CategoryFilterEmptyState } from './CategoryFilterEmptyState';
import {
  buildCategoryRows,
  EmployeeSkillLeafRow,
  EmployeeSkillRow,
} from './employeeSkillCategoryRows';

type Props = {
  employeeSkills: EmployeeSkillAssociation[];
  onChooseSkill?: (skillId: string, isChoosen: boolean) => void;
  maxTopLimit?: number;
  onChangeSkillLevel: (skillId: string, level: SkillLevel) => void;
  onChangeMonthsExperience?: (
    skillId: string,
    monthsExperience: number | null,
  ) => void;
  isSavingMonthsExperience?: (skillId: string) => boolean;
  onRemoveSkill: (skillId: string) => void;
  isReadonly: boolean;
  isSelfView?: boolean;
  showCategoryFilter?: boolean;
};

type StarWrapperProps = {
  isSelected: boolean;
  isReadonly: boolean;
  skill: EmployeeSkillLeafRow;
  onChooseSkill: (skillId: string, isChoosen: boolean) => void;
};

export const EmployeeSkillsTable: React.FC<React.PropsWithChildren<Props>> = ({
  maxTopLimit,
  employeeSkills,
  onChooseSkill,
  onChangeSkillLevel,
  onChangeMonthsExperience,
  isSavingMonthsExperience,
  onRemoveSkill,
  isReadonly,
  isSelfView = false,
  showCategoryFilter = true,
}) => {
  const { t } = useTranslation();

  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  // Grouped is the default view, but only where the toggle to switch back to
  // flat is actually shown — hosts that opt out of the toolbar entirely (e.g.
  // project skills via showCategoryFilter={false}) would otherwise be stuck
  // grouped with no way to change it.
  const [groupByCategory, setGroupByCategory] = useState(showCategoryFilter);

  const uncategorizedLabel = t('skills.grouping.uncategorized');

  // react-table's `Column<D>` generic doesn't handle a discriminated-union `D` well (each
  // accessor's inferred value/row type collapses to one branch); the cast at the bottom of
  // this memo is safe since every Cell guards on `row.original.rowType` before accessing
  // branch-specific fields.
  const columns = useMemo(() => {
    const baseColumns = [];

    if (onChooseSkill) {
      baseColumns.push({
        Header: (
          <Star
            isSelected={false}
            tooltip={t('skills.employeeSkills.table.choosen')}
          />
        ),
        accessor: 'isTop',
        sortType: 'basic',
        Cell: ({ value, row }: CellProps<EmployeeSkillRow>) => {
          if (row.original.rowType !== 'skill') {
            return null;
          }
          return (
            <StarWrapper
              isSelected={value}
              isReadonly={isReadonly}
              skill={row.original}
              onChooseSkill={onChooseSkill}
            />
          );
        },
      });
    }

    baseColumns.push({
      Header: <Trans t={t} i18nKey="skills.employeeSkills.table.skill" />,
      accessor: 'name',
      sortType: 'caseInsensitive',
      Cell: ({ value, row }: CellProps<EmployeeSkillRow>) => {
        if (row.original.rowType === 'category') {
          return (
            <div
              className="d-flex align-items-center gap-2 fw-bold"
              {...row.getToggleRowExpandedProps()}
              role="button">
              <FlairIcon
                icon={
                  row.isExpanded
                    ? 'chevron-down-outline'
                    : 'chevron-forward-outline'
                }
                size="sm"
              />
              <span>{value}</span>
              <span className="text-muted small fw-normal">
                ({row.original.skillCount})
              </span>
            </div>
          );
        }
        return (
          <span
            className="d-inline-flex align-items-center gap-2"
            style={{ paddingLeft: `${row.depth * 1.5}rem` }}>
            <SkillNameWithIcon
              rateable={row.original.isRateable}
              name={value}
            />
            {(row.original.rateLocked || row.original.removeLocked) && (
              <FlairIcon
                icon="lock-closed-outline"
                size="sm"
                tooltip={t('skills.employeeSkills.hrManagedTooltip')}
              />
            )}
          </span>
        );
      },
    });

    if (showCategoryFilter && !groupByCategory) {
      baseColumns.push({
        Header: <Trans t={t} i18nKey="skills.employeeSkills.table.category" />,
        id: 'category',
        accessor: (row: EmployeeSkillRow) =>
          (row.rowType === 'skill' ? row.categoryName : null) ??
          uncategorizedLabel,
        sortType: 'caseInsensitive',
      });
    }

    baseColumns.push({
      Header: <Trans t={t} i18nKey="skills.employeeSkills.table.level" />,
      accessor: 'level',
      sortType: levelSort,
      Cell: ({ row }: CellProps<EmployeeSkillRow>) => {
        if (row.original.rowType !== 'skill') {
          return null;
        }
        return (
          <SkillLevelSelectCell
            isReadonly={isReadonly || row.original.rateLocked}
            skill={row.original}
            onChangeSkillLevel={onChangeSkillLevel}
          />
        );
      },
    });

    baseColumns.push({
      Header: <Trans t={t} i18nKey="skills.employeeSkills.table.experience" />,
      accessor: 'monthsExperience',
      disableSortBy: true,
      Cell: ({ row }: CellProps<EmployeeSkillRow>) => {
        if (row.original.rowType !== 'skill') {
          return null;
        }
        return (
          <MonthsExperienceCell
            isReadonly={
              isReadonly || !onChangeMonthsExperience || row.original.rateLocked
            }
            isSaving={
              isSavingMonthsExperience
                ? isSavingMonthsExperience(row.original.id)
                : false
            }
            skill={row.original}
            onChangeMonthsExperience={onChangeMonthsExperience}
          />
        );
      },
    });

    baseColumns.push({
      Header: '',
      accessor: 'id',
      disableSortBy: true,
      getCellProps: () => {},
      Cell: ({ value, row }: CellProps<EmployeeSkillRow>) => {
        if (row.original.rowType !== 'skill') {
          return null;
        }
        return (
          <div className="text-end">
            <DropdownActions id="skill">
              <DropdownActionItem
                onClick={() => onRemoveSkill(value)}
                disabled={isReadonly || row.original.removeLocked}>
                {t('skills.employeeSkills.unassign')}
              </DropdownActionItem>
            </DropdownActions>
          </div>
        );
      },
    });

    return baseColumns as unknown as Column<EmployeeSkillRow>[];
  }, [
    t,
    onChooseSkill,
    onRemoveSkill,
    onChangeSkillLevel,
    onChangeMonthsExperience,
    isSavingMonthsExperience,
    isReadonly,
    showCategoryFilter,
    groupByCategory,
    uncategorizedLabel,
  ]);

  const topCount = employeeSkills.reduce(
    (acc, cur) => acc + (cur.isTop ? 1 : 0),
    0,
  );
  const isTopLimitReached = maxTopLimit ? topCount >= maxTopLimit : undefined;

  const data: EmployeeSkillRow[] = useMemo(() => {
    const leaves = filterByCategoryIds(employeeSkills, categoryFilter).map(
      (x) => mapRow(x, isTopLimitReached, isSelfView),
    );
    return groupByCategory
      ? buildCategoryRows(leaves, uncategorizedLabel)
      : leaves;
  }, [
    employeeSkills,
    categoryFilter,
    isTopLimitReached,
    isSelfView,
    groupByCategory,
    uncategorizedLabel,
  ]);

  if (!employeeSkills.length) {
    return <Empty />;
  }

  return (
    <>
      {showCategoryFilter && (
        <div className="d-flex gap-3 justify-content-between align-items-center small px-3 pt-3 pb-2">
          <SwitchInput
            id="group-by-category"
            name="groupByCategory"
            label={t('skills.employeeSkills.groupByCategory')}
            checked={groupByCategory}
            onChange={setGroupByCategory}
          />
          <SkillCategoryFilter
            value={categoryFilter}
            onChange={setCategoryFilter}
            className="small"
          />
        </div>
      )}
      {categoryFilter.length > 0 && data.length === 0 ? (
        <CategoryFilterEmptyState
          message={t('skills.employeeSkills.filterHidesAllSkills')}
          onClear={() => setCategoryFilter([])}
        />
      ) : (
        <TableContent
          // react-table only reads `initialSortBy` on mount and doesn't reconcile it when
          // `columns` changes shape later — remount (via `key`) whenever grouping toggles so
          // a stale sort referencing the now-removed `category` column can't carry over and
          // crash the `caseInsensitive` sorter.
          key={groupByCategory ? 'grouped' : 'flat'}
          columns={columns}
          data={data}
          getCellProps={(cellInfo) =>
            cellInfo.column.id === 'name' || cellInfo.column.id === 'category'
              ? {}
              : { className: 'p-0' }
          }
          expandAllRows={groupByCategory}
          tableOptions={{ disableSortBy: groupByCategory }}
          initialSortBy={
            groupByCategory
              ? []
              : showCategoryFilter
              ? [{ id: 'category' }, { id: 'name' }]
              : [{ id: 'name' }]
          }
        />
      )}
    </>
  );
};

const mapRow = (
  src: EmployeeSkillAssociation,
  isTopLimitReached: boolean | undefined,
  isSelfView: boolean,
): EmployeeSkillLeafRow => ({
  rowType: 'skill',
  id: src.id,
  name: src.name,
  isRateable: src.isRateable,
  level: src.isRateable ? src.level : undefined,
  isTop: src.isTop,
  isTopLimitReached,
  rateLocked: isSelfView && !canSelfRate(src.selfServiceMode),
  removeLocked: isSelfView && !canSelfAddRemove(src.selfServiceMode),
  monthsExperience: src.monthsExperience,
  categoryId: src.category?.id ?? null,
  categoryName: src.category?.name ?? null,
});

const SkillLevelSelectCell: React.FC<
  React.PropsWithChildren<{
    isReadonly: boolean;
    skill: EmployeeSkillLeafRow;
    onChangeSkillLevel: (skillId: string, level: SkillLevel) => void;
  }>
> = ({ isReadonly, skill, onChangeSkillLevel }) => {
  const handleOnChange = useCallback(
    (level: SkillLevel) => {
      onChangeSkillLevel(skill.id, level);
    },
    [skill, onChangeSkillLevel],
  );

  if (!skill.isRateable) {
    return null;
  }
  return (
    <SkillLevelSelect
      value={skill.level ?? null}
      isDisabled={isReadonly}
      onChange={handleOnChange}
    />
  );
};

const MonthsExperienceCell: React.FC<
  React.PropsWithChildren<{
    isReadonly: boolean;
    isSaving: boolean;
    skill: EmployeeSkillLeafRow;
    onChangeMonthsExperience?: (
      skillId: string,
      monthsExperience: number | null,
    ) => void;
  }>
> = ({ isReadonly, isSaving, skill, onChangeMonthsExperience }) => {
  const { t } = useTranslation();
  const handleChange = useCallback(
    (value: number | null) => {
      onChangeMonthsExperience?.(skill.id, value);
    },
    [skill.id, onChangeMonthsExperience],
  );

  if (isReadonly) {
    return (
      <span className="small text-muted">
        {formatMonthsExperience(skill.monthsExperience, t)}
      </span>
    );
  }
  return (
    <div className="d-flex align-items-center gap-2">
      <MonthsExperienceInput
        value={skill.monthsExperience}
        onChange={handleChange}
      />
      <AutoSaveIndicator loading={isSaving} />
    </div>
  );
};

const Empty: React.FC<React.PropsWithChildren<unknown>> = () => {
  const t = useNamespacedTranslation('skills.employeeSkills');
  return (
    <div className="d-flex small flex-column align-items-center p-3">
      <EmptyState title={t('empty')} />
    </div>
  );
};

const StarWrapper: React.FC<React.PropsWithChildren<StarWrapperProps>> = ({
  isSelected,
  isReadonly,
  skill,
  onChooseSkill,
}) => {
  const { t } = useTranslation();

  const disabled =
    (!isSelected && skill.isTopLimitReached) ||
    (!isSelected && !skill.level) ||
    !skill.isRateable ||
    isReadonly;

  const tooltip = useMemo(() => {
    if (!isSelected) {
      if (skill.level) {
        if (skill.isTopLimitReached) {
          return t('skills.employeeSkills.maxNumberOfSelectedSkillsTooltip');
        }
      } else {
        if (skill.isRateable) {
          return t('skills.employeeSkills.skillMustHaveLevelTooltip');
        } else {
          return t('skills.employeeSkills.skillMustBeRatedTooltip');
        }
      }
    }
  }, [isSelected, skill, t]);

  return (
    <Star
      isSelected={isSelected}
      disabled={disabled}
      onChange={(val) => onChooseSkill(skill.id, val)}
      tooltip={tooltip}
    />
  );
};

export const levelSort = (
  rowA: Row<EmployeeSkillRow>,
  rowB: Row<EmployeeSkillRow>,
) => {
  const a = rowA.original.rowType === 'skill' ? rowA.original.level : undefined;
  const b = rowB.original.rowType === 'skill' ? rowB.original.level : undefined;

  const aWeight = getSkillLevelSortWeight(a);
  const bWeight = getSkillLevelSortWeight(b);
  return aWeight > bWeight ? 1 : bWeight > aWeight ? -1 : 0;
};

const getSkillLevelSortWeight = (level: SkillLevel | undefined): number => {
  if (level === undefined) {
    return -1;
  }
  if (level === null) {
    return 0;
  }
  return level;
};
